import { useMemo } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import z from 'zod'

import {
  SearchUserParticipantV2Query,
  StatusProfile,
  UpsertUserStatusProfileRequest,
} from '@/_gql/graphql'

import { GqlUpsertUserStatusProfile } from '@/services/admin/gql'
import { gqlFetch } from '@/services/graphql/fetcher'

import { FormItemTemplate } from '@/components/form/template'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
} from '@/components/ui/dialog'
import { Form, FormField } from '@/components/ui/form'
import { FormFieldset } from '@/components/ui/form-field/fieldset'
import { FormFieldSelect } from '@/components/ui/form-field/select'
import { Textarea } from '@/components/ui/textarea'

import { STATUS_PROFILE_OPTIONS } from '../constant'

type User =
  SearchUserParticipantV2Query['searchUserParticipantV2']['data'][number]

interface ChangeStatusDialogProps {
  getData: () => User[]
  onSuccess: () => void
  open: boolean
  setOpen: (state: boolean) => void
}

export function ChangeStatusDialog({
  open,
  setOpen,
  getData,
  onSuccess,
}: ChangeStatusDialogProps) {
  const form = useForm<ChangeStatus>({
    resolver: zodResolver(ChangeStatusDto),
    defaultValues: getDefaultValues(),
  })

  const { mutateAsync } = useMutation({
    mutationFn: async (data: UpsertUserStatusProfileRequest[]) => {
      await gqlFetch({
        query: GqlUpsertUserStatusProfile,
        variables: { request: data },
      })
    },
    onSuccess,
  })

  const data = useMemo(() => {
    if (open) return getData()
    return []
  }, [open, getData])

  const onSubmit = form.handleSubmit(async ({ reason, status }) => {
    mutateAsync(
      data.filter(Boolean).map((d) => ({
        user_id: String(d.id),
        reason,
        status,
      }))
    )
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>Ubah Status</DialogHeader>
        <DialogDescription className="hidden">ubah status</DialogDescription>
        <Form {...form}>
          <div className="flex max-h-44 flex-col divide-y divide-gray-200 overflow-auto rounded-lg border border-gray-200 bg-yellow-300/10">
            {data.map((d) => (
              <div className="p-2" key={d.id}>
                {d.name}
              </div>
            ))}
          </div>
          <div className="mt-4">
            <FormField
              control={form.control}
              name={`status`}
              render={({ field }) => (
                <FormFieldset label="Status" className="w-full">
                  <FormFieldSelect
                    className="w-full"
                    placeholder="Status Profile"
                    items={STATUS_PROFILE_OPTIONS}
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                </FormFieldset>
              )}
            />

            <div
              className={`mt-4 ${status == StatusProfile.SubmissionRejected ? '' : 'hidden'}`}
            >
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItemTemplate.General label="Alasan Penolakan">
                    <Textarea
                      placeholder="Sertakan alasan penolakan di sini"
                      {...field}
                    />
                  </FormItemTemplate.General>
                )}
              />
            </div>
          </div>
          <div className="mt-6 flex justify-between gap-4">
            <Button variant={'outline_yes'} onClick={() => setOpen(false)}>
              Batal
            </Button>
            <Button
              type="submit"
              // onClick={onHandleChangeStatusButton}
            >
              Ubah Status
            </Button>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

interface ChangeStatus extends z.infer<typeof ChangeStatusDto> {}
const ChangeStatusDto = z.object({
  status: z.nativeEnum(StatusProfile),
  reason: z.string().optional(),
})

function getDefaultValues(): ChangeStatus {
  return {
    status: '' as StatusProfile,
    reason: '',
  }
}
